'use client'

import { useEffect, useRef, useState } from 'react'

interface StatItem {
  value: number
  prefix?: string | null
  suffix?: string | null
  label: string
  decimalPlaces: number
}

function formatValue(current: number, decimalPlaces: number): string {
  if (decimalPlaces > 0) {
    const fixed = current.toFixed(decimalPlaces)
    const [intPart, decPart] = fixed.split('.')
    return `${Number(intPart).toLocaleString()}.${decPart}`
  }
  return Math.round(current).toLocaleString()
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

const DURATION = 2000

export function CountUpStats({
  stats,
  stacked,
  variant = 'column',
  compact = false,
}: {
  stats: StatItem[]
  stacked?: boolean
  /** 'column' = Statistics Image=True (single column); 'grid' = Image=False (2-up grid). */
  variant?: 'column' | 'grid'
  /** Figma service-page Statistics instances (e.g. 4080:34515) bind the stat figure to
   *  Headings/headlineMdAlt (32/40/-0.3) instead of Home's headlineLgAlt (40/48/-0.6). */
  compact?: boolean
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const hasAnimated = useRef(false)
  const [currentValues, setCurrentValues] = useState<number[]>(stats.map(() => 0))
  const [started, setStarted] = useState(false)
  const prefersReduced = useRef(false)

  useEffect(() => {
    prefersReduced.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el || hasAnimated.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true
          observer.disconnect()
          if (prefersReduced.current) {
            setCurrentValues(stats.map((s) => s.value))
          } else {
            setStarted(true)
          }
        }
      },
      { threshold: 0.2 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [stats])

  useEffect(() => {
    if (!started) return
    const startTime = performance.now()
    let rafId: number
    function tick() {
      const elapsed = performance.now() - startTime
      const t = Math.min(elapsed / DURATION, 1)
      setCurrentValues(stats.map((s) => easeOutCubic(t) * s.value))
      if (t < 1) rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [started, stats])

  if (stacked) {
    // Embedded variant (used inside Content block columns)
    return (
      <div ref={containerRef} className="flex flex-col gap-8">
        {stats.map((stat, i) => (
          <div key={i}>
            {/* Headings/displayLg → text-display-5xl (64/80/-1 desktop, and the scale
                rescales it for tablet/mobile). Label is Body/bodyLg → text-body (18, with
                line-height bound to font/size/lg). */}
            <p className="font-display text-display-5xl font-normal text-brand tabular-nums">
              {stat.prefix || ''}
              {formatValue(currentValues[i], stat.decimalPlaces)}
              {stat.suffix || ''}
            </p>
            <p className="text-body text-brand mt-2">{stat.label}</p>
          </div>
        ))}
      </div>
    )
  }

  // Headings/headlineLgAlt at every breakpoint → text-display-2xl, which the responsive
  // scale resolves to 32/32/-0.6 mobile, 36/40/-0.6 tablet, 40/48/-0.6 desktop. That is
  // exactly what Figma binds on the Home instance (I4022:82065;2229:2703 → 40/48/-0.6),
  // the tablet (4022:97977) and the mobile (4022:100753) frames, and on Image=False
  // (2229:2735). The former `lg:text-display-xl` (32/40/-0.3, headlineMdAlt) matched only
  // the /sell instance 4080:34515 and made the desktop number one step small everywhere
  // else, so the majority binding wins.
  // Label is Body/bodyMdAlt = text-body-md (16 / leading bound to font/size/lg).
  const numberClass = compact
    ? 'font-display text-display-xl italic font-normal text-foreground tabular-nums'
    : 'font-display text-display-2xl italic font-normal text-foreground tabular-nums'

  // 'column' — Figma 2229:2701: flex column, gap/gap-6 between stats, gap/gap-0 inside.
  // 'grid'   — Image=False. 2 columns on mobile (2229:2770, column gap/gap-4, row gap/gap-6),
  //            a single 4-wide row on tablet (4105:79139, gap/gap-8), back to 2 columns on
  //            desktop (4114:172044, column gap/gap-16, row gap/gap-6 on 2229:2720).
  const listClass =
    variant === 'grid'
      ? 'grid grid-cols-2 gap-x-4 gap-y-6 md:grid-cols-4 md:gap-x-8 lg:grid-cols-2 lg:gap-x-16'
      : 'flex flex-col gap-6'

  return (
    <div ref={containerRef} className={listClass}>
      {stats.map((stat, i) => (
        <div key={i} className="flex min-w-0 flex-col gap-0">
          <p className={numberClass}>
            {stat.prefix || ''}
            {formatValue(currentValues[i], stat.decimalPlaces)}
            {stat.suffix || ''}
          </p>
          <p className="font-base text-body-md font-normal tracking-[0px] text-foreground">
            {stat.label}
          </p>
        </div>
      ))}
    </div>
  )
}
